import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { CheckCircle2, Zap, Car, Wrench, ArrowRight } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const SERVICE_IMAGES = [
  'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=900&q=80',
  'https://images.unsplash.com/photo-1593941707882-a5bba13c2e09?w=900&q=80',
  'https://images.unsplash.com/photo-1595437193398-f24279553f4f?w=900&q=80',
];

const SERVICE_ICONS = [Zap, Car, Wrench];

export default function Services() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const services = [
    { key: 'pv', ...t('services_page.pv', { returnObjects: true }) },
    { key: 'ev', ...t('services_page.ev', { returnObjects: true }) },
    { key: 'repairs', ...t('services_page.repairs', { returnObjects: true }) },
  ];

  const common = t('services_page.common', { returnObjects: true }) || {};

  return (
    <>
      <Helmet>
        <title>Services — ENERGIESSE SL Solar Installation</title>
        <meta
          name="description"
          content="B2B solar installation services: residential PV, EV charger installation, and rapid repairs. SLA-ready, documented, available across Spain and EU."
        />
      </Helmet>

      {/* Page Hero */}
      <section className="pt-14 pb-10 bg-[var(--energy-bg)] border-b border-border">
        <div className="container-site">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="max-w-3xl space-y-4"
          >
            <motion.p variants={fadeUp} className="text-xs font-medium text-[var(--energy-green)] uppercase tracking-widest">
              {t('nav.services')}
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-heading text-3xl md:text-4xl font-semibold tracking-tight"
            >
              {t('services_page.headline')}
            </motion.h1>
            <motion.p variants={fadeUp} className="text-base text-muted-foreground leading-relaxed max-w-[60ch]">
              {t('services_page.subtitle')}
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap gap-2">
              <Badge variant="secondary" className="bg-[var(--energy-green-soft)] text-[var(--energy-green)] border-0">
                {common.b2b_badge}
              </Badge>
              <span className="text-sm text-muted-foreground">{common.geographic_note}</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Services Detail */}
      {services.map((service, i) => {
        const Icon = SERVICE_ICONS[i];
        const isEven = i % 2 === 0;
        return (
          <section
            key={service.key}
            className={`section-padding ${isEven ? 'bg-white' : 'bg-[var(--energy-bg)]'}`}
            data-testid={`service-detail-${service.key}`}
          >
            <div className="container-site">
              <div
                className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${
                  !isEven ? 'lg:grid-flow-dense' : ''
                }`}
              >
                {/* Content */}
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={stagger}
                  className={`space-y-6 ${!isEven ? 'lg:col-start-2' : ''}`}
                >
                  <motion.div variants={fadeUp} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[var(--energy-green-soft)] flex items-center justify-center">
                      <Icon className="h-5 w-5 text-[var(--energy-green)]" />
                    </div>
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      0{i + 1}
                    </span>
                  </motion.div>

                  <motion.h2
                    variants={fadeUp}
                    className="font-heading text-2xl md:text-3xl font-semibold tracking-tight"
                  >
                    {service.headline}
                  </motion.h2>

                  <motion.p variants={fadeUp} className="text-base text-muted-foreground leading-relaxed">
                    {service.intro}
                  </motion.p>

                  <motion.div variants={fadeUp}>
                    <p className="text-xs font-medium text-foreground uppercase tracking-wider mb-3">
                      {service.scope}
                    </p>
                    <ul className="space-y-2">
                      {(service.scope_items || []).map((item, si) => (
                        <li key={si} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                          <CheckCircle2 className="h-4 w-4 text-[var(--energy-green)] mt-0.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </motion.div>

                  <motion.div
                    variants={fadeUp}
                    className="p-4 rounded-xl bg-[var(--energy-green-soft)] border border-[var(--energy-green)]/20"
                  >
                    <p className="text-sm text-[hsl(164,40%,28%)] leading-relaxed">
                      <span className="font-medium">Note: </span>
                      {service.b2b_note}
                    </p>
                  </motion.div>

                  <motion.div variants={fadeUp}>
                    <Button
                      onClick={() => navigate('/contact')}
                      className="bg-[var(--energy-green)] hover:bg-[var(--energy-green)]/90 text-white btn-primary"
                      data-testid={`service-${service.key}-cta`}
                    >
                      {common.contact_cta}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </motion.div>
                </motion.div>

                {/* Image */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.97 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45 }}
                  className={`rounded-2xl overflow-hidden aspect-[4/3] shadow-sm ${
                    !isEven ? 'lg:col-start-1' : ''
                  }`}
                >
                  <img
                    src={SERVICE_IMAGES[i]}
                    alt={service.headline}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </motion.div>
              </div>
            </div>
            {i < services.length - 1 && (
              <div className="container-site mt-14 md:mt-20">
                <Separator />
              </div>
            )}
          </section>
        );
      })}

      {/* CTA */}
      <section className="py-12 bg-[hsl(222,22%,10%)]">
        <div className="container-site">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="space-y-1">
              <h2 className="font-heading text-xl font-semibold text-white">{t('cta_section.title')}</h2>
              <p className="text-sm text-[hsl(215,12%,55%)]">{t('cta_section.subtitle')}</p>
            </div>
            <Button
              size="lg"
              className="bg-[var(--energy-green)] hover:bg-[var(--energy-green)]/90 text-white btn-primary flex-shrink-0"
              onClick={() => navigate('/contact')}
              data-testid="services-cta-button"
            >
              {t('cta_section.cta')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
